<template>
  <div class="tournament-rank-view pa-7" :class="{ 'tournament-rank-embed': isEmbedView }">
    <v-overlay :dark="false" :absolute="true" z-index="10" :value="overlayProcessing"><v-progress-circular
        color="swisetGreen" :size="70" width="5" indeterminate /></v-overlay>

    <!-- Display embed view for embedded/public version -->
    <TournamentEmbedView v-if="isEmbedView && $store.state.tournament" :tournament-data="$store.state.tournament"
      :tournament-rules="tournamentRules" @update-tournament-data="updateTournamentData" />

    <!-- Check if this should render in minimal mode -->
    <template v-else-if="showMinimalView">
      <!-- Minimal View - Only show header, timer and leaderboard -->
      <v-col class="tournament-embed-header px-0">
        <!-- Desktop header layout -->
        <div class="d-none d-sm-block">
          <!-- Show tournament organizer logo when available -->
          <div v-if="$store.state.tournament.imageUrl" class="organizer-logo-container text-center mb-4">
            <img :src="$store.state.tournament.imageUrl" alt="Tournament Organizer" class="organizer-logo" />
          </div>

          <span :class="['status-chip', tournamentStatus.toLowerCase().replace(' ', '-')]" :style="statusChipStyle">
            {{ tournamentStatus.charAt(0).toUpperCase() + tournamentStatus.slice(1).toLowerCase() }}
          </span>
          <div class="d-flex align-center">
            <h1 class="tournament-title mt-4">{{ $store.state.tournament.tournamentName }}</h1>
          </div>
          <h3 class="tournament-organizer">
            {{ $t('header.creatorPrepend') }} {{ $store.state.tournament.tournamentOrganizer }}
          </h3>
        </div>

        <!-- Enhanced Mobile header layout -->
        <div class="mobile-tournament-header d-block d-sm-none">
          <!-- Show tournament organizer logo when available (mobile) -->
          <div v-if="$store.state.tournament.imageUrl" class="organizer-logo-container text-center mb-3">
            <img :src="$store.state.tournament.imageUrl" alt="Tournament Organizer" class="organizer-logo" />
          </div>

          <!-- Tournament title with visual enhancement -->
          <div class="mobile-tournament-title-container">
            <h1 class="mobile-tournament-title">{{ $store.state.tournament.tournamentName }}</h1>
            <div class="mobile-title-underline"></div>
          </div>

          <!-- Organizer info -->
          <h3 class="mobile-tournament-organizer">
            {{ $t('header.creatorPrepend') }} <span class="organizer-name">{{
              $store.state.tournament.tournamentOrganizer }}</span>
          </h3>
        </div>

        <!-- Tournament Timer - positioned better for both desktop and mobile -->
        <v-col v-if="shouldShowTimer" cols="12" class="tournament-timer-container">
          <TournamentTimer :tournamentStartDate="new Date($store.state.tournament.startTime)"
            :tournamentEndDate="new Date($store.state.tournament.endTime)"
            :tournamentInProgress="tournamentStatus === 'IN PROGRESS'"
            :tournamentIsActive="tournamentStatus === 'STILL OPEN' || tournamentStatus === 'IN PROGRESS'"
            :tournamentUserEnrolled="meAsParticipant" />
        </v-col>
      </v-col>

      <!-- Show only the leaderboard -->
      <v-row class="mx-0">
        <v-col cols="12">
          <TournamentStopped v-if="stoppedTournament" />
          <Leaderboard :currentParticipant="meAsParticipant"
            v-else-if="adminMode || ((started || finished) && !inPostRegistrationWindow)" :adminMode="adminMode" />
          <TournamentPostStartRegistration v-else-if="inPostRegistrationWindow"
            :i-am-participant="meAsParticipant !== null"
            :days-post-register="$store.state.tournament.tournamentSettings.daysPostRegister"
            :tournament-start-time="$store.state.tournament.startTime" />
          <TournamentNotStarted v-else :inviteOnly="inviteOnly" @goToParticipants="overlayTournamentParticipants = true"
            :i-am-participant="meAsParticipant !== null" />
        </v-col>
      </v-row>

      <!-- Add buttons with organizer color for minimal view too -->
      <div class="d-flex mt-4 justify-center">
        <button v-if="shouldShowRules" class="desktop-action-btn" @click="overlayTournamentRules = true"
          :style="organizerColorStyle">
          <i class="fa-regular fa-list mr-2"></i>
          {{ $t('viewRules') }}
        </button>
        <button v-if="shouldShowFaqs && $store.state.tournament.faqsUrl" class="desktop-action-btn ml-3"
          @click="goToFaqs" :style="organizerColorStyle">
          <i class="fa-regular fa-circle-question mr-2"></i>
          {{ $t('viewFAQs') }}
        </button>
      </div>
    </template>

    <!-- Regular view for logged-in users - Using new improved layout -->
    <template v-else>
      <!-- Basic tournament header with different mobile presentation -->
      <v-col class="tournament-embed-header px-0">
        <!-- Desktop header layout with admin controls -->
        <div class="d-none d-sm-block">
          <!-- Admin Controls - Redesigned top bar matching Figma -->
          <div v-if="adminUser" class="admin-controls-bar mb-4">
            <div class="admin-controls-left">
              <button class="admin-breadcrumb" @click="goBackToCompetitions">
                <i class="fa-solid fa-chevron-left mr-2"></i>
                {{ $t('goBackToCompetitions') || 'Go back to competitions' }}
              </button>
            </div>
            <div class="admin-controls-right">
              <button class="admin-icon-btn" @click="$store.state.tournament.faqsUrl ? goToFaqs() : showNoFaqsMessage()" :title="$t('viewFAQs') || 'FAQs'">
                <i class="fa-regular fa-circle-question"></i>
              </button>
              <button class="admin-icon-btn" @click="overlayTournamentRules = true" :title="$t('viewRules') || 'Rules'">
                <i class="fa-regular fa-list-ul"></i>
              </button>
              <template v-if="adminMode">
                <span class="admin-divider"></span>
                <button class="admin-icon-btn" :disabled="new Date() > new Date($store.state.tournament.endTime)" @click="goToTournamentSettings" :title="$t('settings') || 'Settings'">
                  <i class="fa-regular fa-gear"></i>
                </button>
                <button class="admin-icon-btn" @click="overlayTournamentShare = true" :title="$t('share') || 'Share'">
                  <i class="fa-regular fa-share-nodes"></i>
                </button>
                <!-- Manual Closure Button for REVIEWING state -->
                <v-btn v-if="tournamentStatus === 'REVIEWING'"
                  style="background-color: var(--v-warning-base); color: white; border: 1px solid var(--v-warning-base);"
                  class="ml-2" small @click="confirmManualClosure">
                  <v-icon small class="mr-1">fa-check-circle</v-icon>
                  {{ $t('finalizeTournament') }}
                </v-btn>
              </template>
              <button class="admin-view-toggle" @click="adminMode = !adminMode">
                {{ adminMode ? $t('viewAsUser') || 'View as User' : $t('viewAsAdmin') }}
                <i class="fa-regular fa-eye ml-2"></i>
              </button>
            </div>
          </div>

          <div class="d-flex align-center mt-4">
            <h1 class="tournament-title">{{ $store.state.tournament.tournamentName }}</h1>
            <span :class="['status-chip', 'ml-3', tournamentStatus.toLowerCase().replace(' ', '-')]" :style="statusChipStyle">
              {{ tournamentStatus.charAt(0).toUpperCase() + tournamentStatus.slice(1).toLowerCase() }}
            </span>
          </div>
          <h3 class="tournament-organizer">
            {{ $t('header.creatorPrepend') }} {{ $store.state.tournament.tournamentOrganizer }}
          </h3>
        </div>

        <!-- Enhanced Mobile header layout -->
        <div class="mobile-tournament-header d-block d-sm-none">
          <!-- Tournament title with visual enhancement -->
          <div class="mobile-tournament-title-container">
            <h1 class="mobile-tournament-title">{{ $store.state.tournament.tournamentName }}</h1>
            <div class="mobile-title-underline"></div>
          </div>

          <!-- Organizer info -->
          <h3 class="mobile-tournament-organizer">
            {{ $t('header.creatorPrepend') }} <span class="organizer-name">{{
              $store.state.tournament.tournamentOrganizer }}</span>
          </h3>

          <!-- Mobile admin controls -->
          <div v-if="adminUser" class="mobile-admin-controls">
            <v-btn icon @click="adminMode = !adminMode">
              <v-icon>{{ adminMode ? 'fa-regular fa-user' : 'fa-cog' }}</v-icon>
            </v-btn>
            <v-btn v-if="adminMode" icon class="ml-2" @click="tournamentSettings">
              <v-icon>fa-cog</v-icon>
            </v-btn>
            <v-btn v-if="adminMode" icon class="ml-2" @click="tournamentParticipants">
              <v-icon>fa-users</v-icon>
            </v-btn>
          </div>
        </div>

        <!-- Tournament Timer - positioned better for both desktop and mobile -->
        <v-col v-if="shouldShowTimer" cols="12" class="tournament-timer-container">
          <TournamentTimer :tournamentStartDate="new Date($store.state.tournament.startTime)"
            :tournamentEndDate="new Date($store.state.tournament.endTime)"
            :tournamentInProgress="tournamentStatus === 'IN PROGRESS'"
            :tournamentIsActive="tournamentStatus === 'STILL OPEN' || tournamentStatus === 'IN PROGRESS'"
            :tournamentUserEnrolled="meAsParticipant" />
        </v-col>
      </v-col>

      <!-- Main content area with improved layout -->
      <v-row>
        <!-- Mobile User Summary - Only shown on mobile -->
        <v-col cols="12" class="show-on-mobile mb-4">
          <div class="mobile-leaderboard-header d-block d-sm-none" style="margin-bottom: 15px;">
            <div class="leaderboard-title-container">
              <i class="fa-regular fa-user mr-2"></i>
              <h2>{{ $t('yourParticipation') }}</h2>
            </div>
          </div>
          <UserSummary v-if="meAsParticipant" :canChangeAcc="!started && !finished && !stoppedTournament"
            :participants="$store.state.tournament.participants" :participant-data="meAsParticipant"
            :ranking-type="$store.state.tournament.tournamentSettings.tournamentRankingType" />
          <div v-else class="no-participant-message">
            <p>{{ $t('notParticipant') }}</p>
          </div>
        </v-col>

        <!-- Tournament content with two column layout -->
        <v-row class="mx-0">
          <!-- Left column - Tournament main content (leaderboard or other state component) -->
          <v-col :cols="12" :lg="adminMode ? 12 : 8" :md="adminMode ? 12 : 8" :sm="12" :xs="12" class="px-0">
            <!-- Tournament Brackets -->
            <TournamentBrackets
              v-if="$store.state.tournament && $store.state.tournament.tournamentId"
              :tournament-id="$store.state.tournament.tournamentId"
              @bracket-selected="handleBracketChange" />

            <TournamentStopped v-if="stoppedTournament" />
            <template v-else-if="adminMode || ((started || finished) && !inPostRegistrationWindow)">
              <Leaderboard :currentParticipant="meAsParticipant" :adminMode="adminMode"
                :tournamentEnded="new Date($store.state.tournament.endTime) <= new Date()"
                @disqualify="onDisqualifyParticipant"
                @activate="onActivateParticipant"
                @see-journal="onSeeJournal"
                @download-members="exportCSV"
                @add-participant="participantsInitialView = 'ADD'; overlayTournamentParticipants = true" />
            </template>
            <TournamentPostStartRegistration v-else-if="inPostRegistrationWindow"
              :i-am-participant="meAsParticipant !== null"
              :days-post-register="$store.state.tournament.tournamentSettings.daysPostRegister"
              :tournament-start-time="$store.state.tournament.startTime" />
            <TournamentNotStarted v-else :inviteOnly="inviteOnly"
              @goToParticipants="overlayTournamentParticipants = true" :i-am-participant="meAsParticipant !== null" />
          </v-col>

          <!-- Right column - User summary and tournament details (only visible in non-admin mode) -->
          <v-col v-if="!adminMode" cols="12" lg="4" md="4" sm="12" xs="12" class="d-flex flex-column">
            <!-- Desktop User Summary (hidden on mobile) -->
            <UserSummary class="hide-on-mobile" :canChangeAcc="!started && !finished && !stoppedTournament"
              :participants="$store.state.tournament.participants" :participant-data="meAsParticipant"
              :ranking-type="$store.state.tournament.tournamentSettings.tournamentRankingType" />

            <!-- Tournament Details Header - Mobile Only -->
            <div class="mobile-leaderboard-header d-block d-sm-none">
              <div class="leaderboard-title-container">
                <i class="fa-regular fa-trophy mr-2"></i>
                <h2>{{ $t('tournamentDetails') }}</h2>
              </div>
            </div>

            <!-- Tournament Awards -->
            <div class="mt-5">
              <TournamentAwards :awards="$store.state.tournament.awards" />
            </div>

            <!-- Tournament Prize Pool -->
            <div class="mt-5">
              <TournamentPrizePool />
            </div>

            <!-- Desktop Footer Actions - Only visible on desktop, hidden for admins (icons in controls bar) -->
            <div v-if="!adminUser" class="desktop-embed-footer d-none d-sm-flex mt-4">
              <button v-if="shouldShowRules" class="desktop-action-btn" @click="overlayTournamentRules = true"
                :style="organizerColorStyle">
                <i class="fa-regular fa-list mr-2"></i>
                {{ $t('viewRules') }}
              </button>
              <button v-if="shouldShowFaqs && $store.state.tournament.faqsUrl" class="desktop-action-btn ml-3"
                @click="goToFaqs" :style="organizerColorStyle">
                <i class="fa-regular fa-circle-question mr-2"></i>
                {{ $t('viewFAQs') }}
              </button>
            </div>
          </v-col>
        </v-row>
      </v-row>

      <!-- Static Mobile Footer with Rules and FAQs (non-fixed) -->
      <div class="mobile-embed-footer d-block d-sm-none">
        <div class="footer-actions">
          <button class="footer-action-btn" @click="overlayTournamentRules = true" :style="organizerColorStyle">
            <i class="fa-regular fa-list mr-2"></i>
            {{ $t('viewRules') }}
          </button>
          <button v-if="$store.state.tournament.faqsUrl" class="footer-action-btn" @click="goToFaqs"
            :style="organizerColorStyle">
            <i class="fa-regular fa-circle-question mr-2"></i>
            {{ $t('viewFAQs') }}
          </button>
        </div>
      </div>
    </template>

    <!-- All overlays kept from original component -->
    <v-overlay :dark="false" v-model="overlayTradesDistribution">
      <TradesDistribution :broker-account="(meAsParticipant !== null) ? meAsParticipant.brokerAccountId : 0"
        :from-date="$store.state.tournament.startTime" :faqs-url="$store.state.tournament.faqsUrl"
        :to-date="$store.state.tournament.endTime"
        :paused="(meAsParticipant !== null) ? meAsParticipant.status === 'PAUSED' : 'PAUSED'"
        @closeWindow="overlayTradesDistribution = false" />
    </v-overlay>

    <!-- <v-overlay :dark="false" v-model="overlayTournamentSettings">
      <TournamentSettings :tournament-summary="$store.state.tournament"
        @closeWindow="overlayTournamentSettings = false" />
    </v-overlay> -->

    <v-overlay :dark="false" v-model="overlayTournamentParticipants">
      <TournamentParticipants :tournament-summary="$store.state.tournament"
        :initial-view="participantsInitialView"
        @closeWindow="overlayTournamentParticipants = false; participantsInitialView = 'GENERAL'" />
    </v-overlay>

    <v-navigation-drawer
      v-model="overlayTournamentShare"
      right
      temporary
      fixed
      hide-overlay
      width="540"
      class="share-drawer"
    >
      <TournamentShare :tournament-id="$store.state.tournament.tournamentId"
        :externalId="$store.state.tournament.tournamentSettings.externalId"
        @closeWindow="overlayTournamentShare = false" @copied="showCopiedSnackbar" @emailSent="handleEmailSent"
        @emailError="handleEmailError" :tournamentStatus="tournamentStatus" />
    </v-navigation-drawer>

    <v-snackbar v-model="snackbar" :timeout="3000" :color="snackbarColor">
      {{ snackbarText }}
      <template v-slot:action="{ attrs }">
        <v-btn text v-bind="attrs" @click="snackbar = false">
          {{ $t('close_TournamentOverview') }}
        </v-btn>
      </template>
    </v-snackbar>

    <v-navigation-drawer
      v-model="overlayTournamentRules"
      right
      temporary
      fixed
      hide-overlay
      width="460"
      class="rules-drawer"
    >
      <RulesDialog :rules="formattedRulesHtml" :admin-mode="adminMode" @close="overlayTournamentRules = false" />
    </v-navigation-drawer>

    <!-- Disqualify/Activate Confirmation Dialog -->
    <v-dialog v-model="showDisqualifyDialog" max-width="450px" persistent>
      <v-card class="disqualify-confirm-dialog">
        <v-card-title class="d-flex align-center">
          <v-avatar size="50" class="mr-3">
            <v-img :src="disqualifyTarget && disqualifyTarget.profilePicture ? disqualifyTarget.profilePicture : 'https://upload.wikimedia.org/wikipedia/commons/d/d3/User_Circle.png'" />
          </v-avatar>
          <span style="font-family: 'Aeonik', 'Avenir', sans-serif;">
            {{ disqualifyAction === 'DISQUALIFY' ? ($t('confirmDisqualify') || 'Disqualify Participant') : ($t('confirmActivate') || 'Activate Participant') }}
          </span>
        </v-card-title>
        <v-card-text v-if="disqualifyTarget" style="font-family: 'Avenir', sans-serif;">
          <p>{{ disqualifyAction === 'DISQUALIFY'
            ? ($t('confirmDisqualifyDesc', { name: disqualifyTarget.firstName }) || `Are you sure you want to disqualify ${disqualifyTarget.firstName}?`)
            : ($t('confirmActivateDesc', { name: disqualifyTarget.firstName }) || `Are you sure you want to re-activate ${disqualifyTarget.firstName}?`)
          }}</p>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="showDisqualifyDialog = false">{{ $t('btnCancel') || 'Cancel' }}</v-btn>
          <v-btn :color="disqualifyAction === 'DISQUALIFY' ? 'error' : 'success'" :loading="processingAction" @click="executeStatusChange">
            {{ disqualifyAction === 'DISQUALIFY' ? ($t('btnDisqualify') || 'Disqualify') : ($t('btnActivate') || 'Activate') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Manual Closure Confirmation Dialog -->
    <v-dialog v-model="showClosureConfirmation" max-width="500px" persistent>
      <v-card class="manual-closure-dialog">
        <v-card-title class="manual-closure-title">
          <v-icon color="warning" class="mr-2">fa-exclamation-triangle</v-icon>
          <span style="font-family: 'Aeonik', 'Avenir', sans-serif !important;">{{ $t('confirmManualClosure.title')
            }}</span>
        </v-card-title>
        <v-card-text class="manual-closure-body">
          <p class="mb-3" style="font-family: 'Avenir', sans-serif !important;">{{ $t('confirmManualClosure.message') }}
          </p>
          <v-alert type="warning" dense outlined class="warning-alert">
            <span style="font-family: 'Avenir', sans-serif !important;">{{ $t('confirmManualClosure.warning') }}</span>
          </v-alert>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="showClosureConfirmation = false" class="cancel-btn">
            {{ $t('btnCancel') }}
          </v-btn>
          <v-btn color="warning" @click="manuallyCloseTournament" :loading="closingTournament" class="confirm-btn">
            <v-icon small class="mr-1">fa-check</v-icon>
            {{ $t('confirmManualClosure.confirm') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import Vue from 'vue';
import { mapMutations } from 'vuex';
import { setLocale, i18nEventBus } from '@/i18n';
import Leaderboard from "@/views/Tournament/components/Leaderboard";
import Repository from '../../../repositories/tournament';
import UserSummary from "@/views/Tournament/components/UserSummary";
import TournamentTimer from "@/components/TournamentTimer";
import TradesDistribution from "@/views/Tournament/components/TradesDistribution";
import TournamentSettings from "@/views/Tournament/components/TournamentSettings";
import TournamentParticipants from "@/views/Tournament/components/TournamentParticipants";
import TournamentNotStarted from "@/views/Tournament/components/TournamentNotStarted.vue";
import TournamentPostStartRegistration from "@/views/Tournament/components/TournamentPostStartRegistration.vue";
import TournamentAwards from "@/views/Tournament/components/TournamentAwards";
import TournamentPrizePool from "@/views/Tournament/components/TournamentPrizePool";
import TournamentStopped from "@/views/Tournament/components/TournamentStopped.vue";
import TournamentBrackets from "@/views/Tournament/components/TournamentBrackets";
import TournamentShare from "@/views/Tournament/components/TournamentShare";
import TournamentEmbedView from "@/views/Tournament/components/TournamentEmbedView";
import RulesDialog from "@/views/Tournament/components/RulesDialog.vue";

export default {
  name: "TournamentOverview",
  components: {
    TournamentStopped,
    TournamentAwards,
    TournamentPrizePool,
    TournamentNotStarted,
    TournamentPostStartRegistration,
    TournamentParticipants,
    TournamentSettings,
    TradesDistribution,
    TournamentTimer,
    UserSummary,
    Leaderboard,
    TournamentShare,
    TournamentEmbedView,
    RulesDialog,
    TournamentBrackets
  },
  props: {
    tournamentId: Number,
  },
  watch: {
    "$store.state.tournament": {
      deep: true,
      immediate: true,
      handler() {
        this.verifyMeAsParticipant();
        this.getTournamentRules();
      }
    },
    // Watch for query param changes to handle navigation between tournaments
    "$route.query.tournamentId": {
      handler(newId, oldId) {
        // Only reload if the tournament ID actually changed and we have a new ID
        if (newId && newId !== oldId) {
          this.init();
        }
      }
    },
    overlayTournamentRules(open) {
      // Hide/show external chat widget (rendered in iframe, CSS can't reach it)
      const chatElements = document.querySelectorAll('#hubspot-messages-iframe-container, iframe[id*="chat"], iframe[id*="hotline"], [class*="d-hotline"], #drift-widget, #intercom-container, #launcher, .intercom-lightweight-app');
      chatElements.forEach(el => {
        el.style.display = open ? 'none' : '';
      });
      // Also try to find any iframe containing the chat widget
      document.querySelectorAll('iframe').forEach(iframe => {
        try {
          const iframeDoc = iframe.contentDocument || iframe.contentWindow.document;
          if (iframeDoc && iframeDoc.querySelector('.d-hotline')) {
            iframe.style.display = open ? 'none' : '';
          }
        } catch (e) {
          // Cross-origin iframe, check by attributes
          if (iframe.className.includes('hotline') || iframe.id.includes('hotline') || iframe.src.includes('hotline') || iframe.src.includes('desku') || iframe.src.includes('chat')) {
            iframe.style.display = open ? 'none' : '';
          }
        }
      });
    },
    // Watch for prop changes (used by broker app's CompetitionOverview)
    tournamentId: {
      handler(newId, oldId) {
        // Only reload if the tournament ID prop changed and we have a new ID
        if (newId && newId !== oldId) {
          console.log('Tournament ID prop changed from', oldId, 'to', newId);
          this.init();
        }
      }
    }
  },
  data() {
    return {
      overlayProcessing: false,
      started: false,
      finished: false,
      inPostRegistrationWindow: false,
      secsAutoRefresh: 0,
      adminUser: false,
      meAsParticipant: null,
      overlayTradesDistribution: false,
      overlayTournamentSettings: false,
      overlayTournamentParticipants: false,
      participantsInitialView: 'GENERAL',
      overlayTournamentShare: false,
      snackbar: false,
      snackbarText: '',
      snackbarColor: 'mainGreen',
      isMobile: false,
      adminMode: false,
      tournamentRules: undefined,
      overlayTournamentRules: false,
      // Track current loading request to prevent race conditions
      currentLoadingTournamentId: null,
      isEmbedView: false,
      currentLocale: null, // Add this new property to track language changes
      refreshKey: 0, // Add this to force component re-render
      showClosureConfirmation: false,
      closingTournament: false,
      showDisqualifyDialog: false,
      disqualifyTarget: null,
      disqualifyAction: 'DISQUALIFY',
      processingAction: false
    };
  },
  computed: {
    // New computed property to check if we should show the minimal view
    showMinimalView() {
      // Check for tournamentOrganizerId = 44
      if (this.$store.state.tournament && (this.$store.state.tournament.tournamentOrganizerId === 137)) {
        return true;
      }

      // Check for clubMember.club.id = 137
      if (this.$store.state.clubMember &&
        this.$store.state.clubMember.club &&
        (this.$store.state.clubMember.club.id === 137)) {
        return true;
      }

      return false;
    },
    stoppedTournament() {
      return (this.started || this.finished) && this.$store.state.tournament.participants.length < this.$store.state.tournament.minsize ? this.$store.state.tournament.minsize : 0;
    },
    inviteOnly() {
      return this.$store.state.tournament.inviteOnly && !this.started && !this.meAsParticipant;
    },
    tournamentStatus() {
      return this.getTournamentStatus();
    },
    isPublicView() {
      return this.$route.name === 'tournament-embed-rank';
    },
    formattedRulesHtml() {
      if (!this.tournamentRules || !this.tournamentRules.length) {
        return null;
      }

      // Convert tournament rules array to HTML string for RulesDialog component
      let rulesHtml = `<p class="mb-5">${this.$t('rulesDescription')}</p><ul class="rules-list">`;

      this.tournamentRules.forEach(rule => {
        rulesHtml += `<li class="rule-item">
          <strong>${rule.description}</strong>: ${rule.value}
        </li>`;
      });

      rulesHtml += '</ul>';
      return rulesHtml;
    },
    // New computed property for organizer color styling
    organizerColorStyle() {
      const colors = this.$store.state.tournament?.tournamentSettings?.colors;
      if (colors && (colors.primary || colors.secondary)) {
        const isDark = this.$vuetify.theme.dark;
        const color = isDark && colors.secondary ? colors.secondary : colors.primary;
        if (!color) return {};
        const cleanColor = color.replace('#', '');
        const fullColor = `#${cleanColor}`;
        return {
          backgroundColor: fullColor,
          borderColor: fullColor,
          color: this.getContrastYIQ(cleanColor) === 'black' ? '#000' : '#fff'
        };
      }
      return {};
    },
    shouldShowTimer() {
      const selectedFeatures = this.$store.state.tournament?.selectedFeatures;
      // If selectedFeatures doesn't exist (old tournaments), show all features
      if (!selectedFeatures || !Array.isArray(selectedFeatures)) {
        return true;
      }
      return selectedFeatures.includes('TOURNAMENT_RANKING_COUNTER');
    },
    shouldShowRules() {
      const selectedFeatures = this.$store.state.tournament?.selectedFeatures;
      // If selectedFeatures doesn't exist (old tournaments), show all features
      if (!selectedFeatures || !Array.isArray(selectedFeatures)) {
        return true;
      }
      return selectedFeatures.includes('TOURNAMENT_RANKING_RULES');
    },
    shouldShowFaqs() {
      const selectedFeatures = this.$store.state.tournament?.selectedFeatures;
      // If selectedFeatures doesn't exist (old tournaments), show all features
      if (!selectedFeatures || !Array.isArray(selectedFeatures)) {
        return true;
      }
      return selectedFeatures.includes('TOURNAMENT_RANKING_FAQS');
    },

    // Computed property for status chip styling with YIQ contrast
    statusChipStyle() {
      const status = this.tournamentStatus.toLowerCase().replace(' ', '-');
      let backgroundColor = '';

      switch (status) {
        case 'still-open':
          backgroundColor = 'FFF75F';
          break;
        case 'in-progress':
          // Use the green color value - you may need to adjust this to match your CSS variable
          backgroundColor = '00F784'; // This should match your --v-mainGreen-base value
          break;
        case 'reviewing':
          backgroundColor = 'FF9800'; // Orange color for pending/reviewing status
          break;
        case 'closed':
          backgroundColor = '111111';
          break;
        case 'archived':
          backgroundColor = '6c757d';
          break;
        default:
          backgroundColor = 'f8f8f8';
      }

      const textColor = this.getContrastYIQ(backgroundColor);

      return {
        color: textColor === 'black' ? '#000000' : '#ffffff'
      };
    },
  },
  async mounted() {
    // Listen for language change events
    i18nEventBus.$on('language-changed', () => {
      // Force re-render of the entire component tree by changing the key
      this.refreshKey += 1;
    });

    this.isEmbedView = this.$route.name === 'tournament-embed-rank';
    this.identifier = this.$route.query.identifier || null;
    await this.init();
    window.addEventListener("resize", () => {
      let mobile = window.innerWidth <= 1290 ? true : false;
      this.isMobile = mobile;
    });
    await this.getTournamentRules();
  },
  beforeRouteLeave(to, from, next) {
    if (to.name !== 'tournament-settings') {
      this.resetTournament();
    }
    next();
  },
  destroyed() {
    // Remove the event listener
    i18nEventBus.$off('language-changed');
    // Restore chat widget visibility if hidden
    if (this.overlayTournamentRules) {
      this.overlayTournamentRules = false;
    }
  },
  methods: {
    init() {
      this.overlayProcessing = true;

      this.isMobile = window.innerWidth <= 1290 ? true : false;

      // Reset any existing tournament state before loading new one
      // This prevents stale data from showing momentarily
      this.resetTournament();

      this.getTournamentOverview();
    },
    getTournamentOverview() {
      // Get tournament ID from query param (primary) or prop (for broker app)
      // IMPORTANT: Do NOT fall back to store state - it may contain stale data
      let id = this.$route.query.tournamentId;
      if (!id || id == null) {
        // Try to get ID from component prop (used by broker app wrapper)
        if (this.tournamentId !== undefined) {
          id = this.tournamentId;
        }
      }

      // If we don't have an ID, redirect to tournaments list
      if (!id) {
        console.error('No tournament ID available in query params or props');
        this.$router.push('/tournaments');
        return;
      }

      // Store the ID we're loading to detect race conditions (only for numeric IDs)
      const numericId = Number(id);
      const isNumericId = !isNaN(numericId) && isFinite(numericId);
      const loadingId = isNumericId ? numericId : id;
      
      // Only set currentLoadingTournamentId for numeric IDs to enable race condition guard
      if (isNumericId) {
        this.currentLoadingTournamentId = loadingId;
      } else {
        // For string IDs, reset to null to disable race condition guard
        this.currentLoadingTournamentId = null;
      }

      let isFromPublic = this.$route.name === 'tournament-embed-rank';
      this.isEmbedView = isFromPublic;

      if (isFromPublic) {
        let identifier = this.$route.query.identifier;
        this.identifier = identifier;
        Repository.getTournamentOverviewOpen(id, identifier).then((response) => {
          // Race condition guard: only process if this is still the current request (only for numeric IDs)
          if (isNumericId && this.currentLoadingTournamentId !== loadingId) {
            console.log('Ignoring stale response for tournament', loadingId, '- current is', this.currentLoadingTournamentId);
            return;
          }
          if (response.status < 400) {
            this.overlayProcessing = false;
            // Public endpoint should already be in the correct format, but normalize just in case
            const normalizedData = this.normalizeAdminResponse(response.data, false);
            this.computeTournamentBasics(normalizedData);
          }
        });
        return;
      }
      // Check if user is an admin in club context to call appropriate endpoint
      const isClubAdmin = this.$store.getters['broker/authToken'] || (this.$store.state.clubMember &&
        (this.$store.state.clubMember.rol === 'ADMIN' || this.$store.state.clubMember.rol === 'FOUNDER'));

      const apiCall = isClubAdmin
        ? Repository.getTournamentOverviewAdmin(id)
        : Repository.getTournamentOverview(id);

      apiCall.then((response) => {
        // Race condition guard: only process if this is still the current request (only for numeric IDs)
        if (isNumericId && this.currentLoadingTournamentId !== loadingId) {
          console.log('Ignoring stale response for tournament', loadingId, '- current is', this.currentLoadingTournamentId);
          return;
        }
        if (response.status < 400) {
          this.overlayProcessing = false;
          // Normalize the admin response if it has the nested structure
          const normalizedData = this.normalizeAdminResponse(response.data, isClubAdmin);
          this.computeTournamentBasics(normalizedData);
          // eslint-disable-next-line no-console
          console.log("tournament overview", normalizedData);
        }
      });
    },
    ...mapMutations(['setTournament', 'resetTournament', 'setShare']),
    normalizeAdminResponse(data, isAdminCall) {
      // If this is from the admin endpoint and has the nested structure, normalize it
      if (isAdminCall && data.tournamentSettings && data.tournamentSettings.tournament) {
        const tournament = data.tournamentSettings.tournament;

        // Flatten the structure to match the regular endpoint format
        return {
          tournamentId: tournament.id,
          tournamentName: tournament.name,
          tournamentOrganizer: tournament.creator ? tournament.creator.name : '',
          tournamentOrganizerId: tournament.creator ? tournament.creator.id : null,
          startTime: tournament.startTime,
          endTime: tournament.endTime,
          imageUrl: tournament.imageUrl || data.tournamentSettings.imageUrl,
          faqsUrl: tournament.faqsUrl || data.tournamentSettings.faqsUrl || '',
          inviteOnly: tournament.inviteOnly,
          tournamentVisibility: tournament.tournamentVisibility,
          initialBalance: tournament.initialBalance,
          minsize: data.tournamentSettings.minsize,
          tournamentSettings: data.tournamentSettings,
          selectedFeatures: data.selectedFeatures || [],
          awards: data.awards || [],
          participants: data.participants || [],
          secsAutoRefresh: tournament.assessment ? tournament.assessment.secsAutoRefresh : null
        };
      }

      // Return data as-is if it's already in the correct format
      return data;
    },
    computeTournamentBasics(tournamentOverview) {
      this.setTournament(tournamentOverview);
      let now = new Date();

      // Set language based on tournament organizer ID
      this.setLanguageBasedOnOrganizer();

      if (new Date(this.$store.state.tournament.endTime) < now) { // Ended tournament
        this.finished = true;
      }
      else if (new Date(this.$store.state.tournament.startTime) < now) {// Running tournament
        this.started = true;

        // Check if we're in the post-registration window
        this.checkPostRegistrationWindow();

        if (tournamentOverview.secsAutoRefresh !== null && tournamentOverview.secsAutoRefresh > 60) {
          this.secsAutoRefresh = tournamentOverview.secsAutoRefresh;
          // eslint-disable-next-line no-console
          console.log(`Calling tournament overview at: ${(new Date())}`);
          this.setTimerRealTimeEmulation();
        }
      }

      this.verifyMeAsParticipant();
      this.verifyIfAdmin();
      this.handleActionParam();
    },
    checkPostRegistrationWindow() {
      const daysPostRegister = this.$store.state.tournament?.tournamentSettings?.daysPostRegister || 0;

      // Only check if daysPostRegister is set and greater than 0
      if (daysPostRegister > 0) {
        const now = new Date();
        const startTime = new Date(this.$store.state.tournament.startTime);
        const postRegistrationEndTime = new Date(startTime.getTime() + (daysPostRegister * 24 * 60 * 60 * 1000));

        // Only show post-registration window if:
        // 1. We're within the registration time window
        // 2. User is NOT yet a participant (if they are, show leaderboard instead)
        this.inPostRegistrationWindow = !this.adminMode &&
          now >= startTime &&
          now <= postRegistrationEndTime &&
          !this.meAsParticipant;
      } else {
        this.inPostRegistrationWindow = false;
      }
    },
    verifyMeAsParticipant() {
      let foundParticipant = null;

      if (this.identifier != null) {
        // Match by broker account identifier (public/embed view)
        foundParticipant = this.$store.state.tournament.participants.find(
          (participant) => participant.brokerAccountIdentifier === this.identifier);
      } else {
        // Try to match by userId first, then fallback to username
        foundParticipant = this.$store.state.tournament.participants.find(
          (participant) => participant.userId === this.$store.state.user.id);

        // Fallback: match by username if userId is not available in participant data
        if (!foundParticipant && this.$store.state.user.username) {
          foundParticipant = this.$store.state.tournament.participants.find(
            (participant) => participant.username === this.$store.state.user.username);
        }
      }

      this.meAsParticipant = foundParticipant || null;

      // Re-check post-registration window when participant status changes
      if (this.started) {
        this.checkPostRegistrationWindow();
      }
    },
    verifyIfAdmin() {
      if (this.$store.state.tournament !== null && this.$store.state.clubMember !== null)
        this.adminUser = (this.$store.state.tournament.tournamentOrganizerId === this.$store.state.clubMember.club.id &&
          (this.$store.state.clubMember.rol === 'FOUNDER') || (this.$store.state.clubMember.rol === 'ADMIN'));
      if (this.$store.getters['broker/authToken'])
        this.adminUser = true;
    },
    handleActionParam() {
      const action = this.$route.query.action;
      if (!action || !this.adminUser) return;

      this.adminMode = true;

      if (action === 'share') {
        // Clean the action param, then open share overlay
        const query = { ...this.$route.query };
        delete query.action;
        this.$router.replace({ query });
        this.$nextTick(() => {
          this.overlayTournamentShare = true;
        });
      } else if (action === 'settings') {
        // Replace the current history entry so back navigation skips the overview with ?action=settings
        const tournament = this.$store.state.tournament;
        this.$router.replace({
          name: 'tournament-settings',
          params: {
            tournamentId: tournament.tournamentId,
            tournamentSummary: tournament
          }
        });
      }
    },
    setTimerRealTimeEmulation() {
      setTimeout(this.getTournamentOverview, (this.secsAutoRefresh * 1000))
    },
    seeDistribution() {
      this.overlayTradesDistribution = true;
    },
    tournamentSettings() {
      this.overlayTournamentSettings = true;
    },
    tournamentParticipants() {
      this.overlayTournamentParticipants = true;
    },
    showCopiedSnackbar() {
      this.snackbarText = this.$t('copiedToClipboard');
      this.snackbarColor = 'darkGreen';
      this.snackbar = true;
    },
    handleEmailSent(email) {
      this.snackbarText = this.$t('emailSentSuccess', { email });
      this.snackbarColor = 'darkGreen';
      this.snackbar = true;
      this.overlayTournamentShare = false;
    },
    handleEmailError(errorMsg) {
      this.snackbarText = errorMsg;
      this.snackbarColor = 'error';
      this.snackbar = true;
    },
    getTournamentStatus() {
      // Check if tournament has a specific state (like REVIEWING)
      const tournamentState = this.$store.state.tournament?.tournamentSettings?.tournamentState;
      if (tournamentState === 'REVIEWING') {
        return 'REVIEWING';
      }

      const now = new Date();
      const startTime = new Date(this.$store.state.tournament.startTime);
      const endTime = new Date(this.$store.state.tournament.endTime);

      if (now < startTime) {
        return 'STILL OPEN';
      } else if (now >= startTime && now <= endTime) {
        return 'IN PROGRESS';
      } else {
        return 'CLOSED';
      }
    },
    getRuleNamenValue(rule) {
      switch (rule.ruleType) {
        case 'MAX_DRAWDOWN_BL_PT':
        case 'MAX_DRAWDOWN_RT':
        case 'MAX_DRAWDOWN':
          // Convert decimal to percentage (e.g., 0.3 -> 30%)
          return {
            name: this.$t('maxDrawdown'),
            value: `${(rule.value * 100).toFixed(0)}%`
          };
        case 'CONSISTENCY':
          // Convert decimal to percentage (e.g., 0.4 -> 40%)
          return {
            name: this.$t('consistency'),
            value: `${(rule.value * 100).toFixed(0)}%`
          };
        case 'MIN_TRADES':
          return {
            name: this.$t('minTrades'),
            value: rule.value
          };
        case 'OVERNIGHT':
          // Check if overnight trading is allowed
          return {
            name: this.$t('overnight'),
            value: rule.value > 0 ? this.$t('yes') : this.$t('no')
          };
        case 'POSITIVE_BALANCE':
          // Format as currency
          return {
            name: this.$t('positiveBalance'),
            value: `USD $${rule.value.toFixed(2)}`
          };
        case 'MAX_TRADE_SIZE':
          // Format large numbers with commas for readability
          return {
            name: this.$t('maxTradeSize'),
            value: `${rule.value.toLocaleString('en-US', { maximumFractionDigits: 0 })} ${this.$t('lots')}`
          };
        case 'MAX_DRAWDOWN_EQ_PT':
          // Max. Equity Drawdown - percentage (e.g., 0.3 -> 30%)
          return {
            name: this.$t('maxEquityDrawdown'),
            value: `${(rule.value * 100).toFixed(0)}%`
          };
        case 'POSITIVE_EQUITY':
          // Boolean: positive equity required
          return {
            name: this.$t('positiveEquity'),
            value: rule.value >=0 ? this.$t('yes') : this.$t('no')
          };
        case 'MIN_TRADABLE_DAYS':
          return {
            name: this.$t('minTradableDays'),
            value: rule.value
          };
        default:
          return {
            name: rule.ruleType,
            value: rule.value
          };
      }
    },
    async getTournamentRules() {
      // Guard: Don't fetch rules if tournament is not loaded yet
      if (!this.$store.state.tournament || !this.$store.state.tournament.tournamentId) {
        return;
      }

      let rules;
      await Repository.getTournamentRules(this.$store.state.tournament.tournamentId).then((res) => {
        rules = res.data.map(rule => {
          let ruleFormatted = this.getRuleNamenValue(rule)
          return {
            ruleId: rule.ruleType,
            description: ruleFormatted.name,
            value: ruleFormatted.value,
          }
        });
      });
      this.tournamentRules = rules;
    },
    goToFaqs() {
      window.open(this.$store.state.tournament.faqsUrl, '_blank');
    },
    showNoFaqsMessage() {
      this.snackbarText = this.$t('noFaqsConfigured') || 'No FAQs URL configured for this tournament';
      this.snackbarColor = 'warning';
      this.snackbar = true;
    },
    async handleBracketChange(bracket) {
      // When bracket changes, fetch the tournament overview for the selected bracket's tournament
      this.overlayProcessing = true;
      try {
        const response = await Repository.getTournamentOverviewAdmin(bracket.tournamentId);

        if (response.status === 200) {
          // Update the tournament data in the store with the new bracket's tournament
          const tournamentData = response.data;

          // For brackets, use master tournament info from the bracket data
          const masterName = bracket.masterName;
          const masterStartTime = bracket.tournamentStartTime;
          const masterEndTime = bracket.tournamentEndTime;
          const originalStatus = this.$store.state.tournament.tournamentStatus;
          const originalOrganizer = this.$store.state.tournament.tournamentOrganizer;
          const originalImageUrl = this.$store.state.tournament.imageUrl;

          // Update tournament data with bracket-specific info
          this.setTournament({
            ...tournamentData.tournamentSettings.tournament,
            tournamentId: bracket.tournamentId,
            tournamentName: masterName, // Use master tournament name
            startTime: masterStartTime, // Use master start time
            endTime: masterEndTime, // Use master end time
            tournamentStatus: originalStatus, // Keep original status
            tournamentOrganizer: originalOrganizer,
            imageUrl: originalImageUrl,
            tournamentSettings: tournamentData.tournamentSettings,
            awards: tournamentData.awards,
            participants: tournamentData.participants,
            selectedFeatures: tournamentData.selectedFeatures
          });

          // Force refresh of child components by triggering a re-render
          this.$forceUpdate();
        }
      } catch (error) {
        console.error('Error fetching bracket tournament data:', error);
      } finally {
        this.overlayProcessing = false;
      }
    },
    updateTournamentData(tournamentData) {
      // Update tournament data from TournamentEmbedView when bracket changes
      this.setTournament(tournamentData);
      this.$forceUpdate();
    },
    // Helper method to determine if text should be black or white based on background color
    getContrastYIQ(hexcolor) {
      if (!hexcolor) return 'black';

      // Remove hash if it exists
      hexcolor = hexcolor.replace('#', '');

      const r = parseInt(hexcolor.substr(0, 2), 16);
      const g = parseInt(hexcolor.substr(2, 2), 16);
      const b = parseInt(hexcolor.substr(4, 2), 16);

      const yiq = ((r * 299) + (g * 587) + (b * 114)) / 1000;

      return (yiq >= 128) ? 'black' : 'white';
    },
    // Completely rewritten to match the working LanguageSelector approach
    setLanguageBasedOnOrganizer() {
      if (!this.$store.state.tournament || !this.$store.state.tournament.tournamentOrganizerId) {
        return;
      }

      const organizerId = this.$store.state.tournament.tournamentOrganizerId;

      const torunamentId = this.$store.state.tournament.tournamentId;

      // Use localStorage instead of sessionStorage for persistence
      const languageSetKey = `language_set_for_${organizerId}`;



      // If language has already been set for this tournament, don't change it again
      if (localStorage.getItem(languageSetKey) === 'true') {
        return;
      }

      // Check if user has explicitly changed the language
      if (localStorage.getItem(`user_changed_locale_${organizerId}`)) {
        localStorage.setItem(languageSetKey, 'true');
        return;
      }

      // Get target locale based on organizer ID
      let targetLocale = null;
      if (organizerId === 136) {
        targetLocale = 'en';
      } else if (organizerId === 137) {
        if (torunamentId === 390 || torunamentId === 389) {
          targetLocale = 'es';
        } else {
          targetLocale = 'zh-Hant';
        }
      }

      else {
        // No forced language for other organizers
        localStorage.setItem(languageSetKey, 'true');
        return;
      }

      // Skip if already using the correct locale
      if (this.$i18n.locale === targetLocale) {
        localStorage.setItem(languageSetKey, 'true');
        return;
      }

      // Mark as processed BEFORE changing the language
      localStorage.setItem(languageSetKey, 'true');

      // Do exactly what LanguageSelector does:
      // 1. Set the locale
      setLocale(targetLocale);

      // 2. Update the store
      this.$store.commit('setPreferredLanguage', targetLocale);

      // 3. Delay and reload - crucial for proper language application
      setTimeout(() => {
        window.location.reload();
      }, 100);
    },
    confirmManualClosure() {
      this.showClosureConfirmation = true;
    },
    goToTournamentSettings() {
      const tournament = this.$store.state.tournament;
      // Pass both the tournament summary and the tournament ID for reliability
      this.$router.push({
          name: 'tournament-settings',
          params: {
            tournamentId: tournament.tournamentId,
            tournamentSummary: tournament
          }
      });
    },
    goBackToCompetitions() {
      // Navigate directly to the club tournaments list instead of relying on history
      if (this.$store.state.clubMember) {
        this.$router.push({ name: 'club-tournaments' });
      } else {
        this.$router.go(-1);
      }
    },
    async manuallyCloseTournament() {
      this.closingTournament = true;
      const tournamentId = this.$store.state.tournament.tournamentId;

      try {
        // Call API to update tournament state to FINISHED
        const response = await Repository.updateTournamentState(tournamentId, 'FINISHED');

        if (response.status < 400) {
          this.snackbarText = this.$t('tournamentClosed');
          this.snackbarColor = 'success';
          this.snackbar = true;
          this.showClosureConfirmation = false;

          // Refresh tournament data
          setTimeout(() => {
            this.getTournamentOverview();
          }, 1000);
        } else {
          throw new Error('Failed to close tournament');
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error closing tournament:', error);
        this.snackbarText = this.$t('errorClosingTournament');
        this.snackbarColor = 'error';
        this.snackbar = true;
      } finally {
        this.closingTournament = false;
      }
    },
    onDisqualifyParticipant(participant) {
      this.disqualifyTarget = participant;
      this.disqualifyAction = 'DISQUALIFY';
      this.showDisqualifyDialog = true;
    },
    onActivateParticipant(participant) {
      this.disqualifyTarget = participant;
      this.disqualifyAction = 'ACTIVATE';
      this.showDisqualifyDialog = true;
    },
    async executeStatusChange() {
      if (!this.disqualifyTarget) return;
      this.processingAction = true;
      const newStatus = this.disqualifyAction === 'DISQUALIFY' ? 'DISQUALIFIED' : 'ACTIVE';
      try {
        const response = await Repository.changeParticipantStatus(this.disqualifyTarget.participantId, newStatus);
        if (response.status < 400) {
          this.snackbarText = this.disqualifyAction === 'DISQUALIFY'
            ? (this.$t('participantDisqualified') || 'Participant disqualified')
            : (this.$t('participantActivated') || 'Participant activated');
          this.snackbarColor = 'mainGreen';
          this.snackbar = true;
          this.showDisqualifyDialog = false;
          // Refresh tournament data
          this.getTournamentOverview();
        }
      } catch (error) {
        console.error('Error changing participant status:', error);
        this.snackbarText = this.$t('errorChangingStatus') || 'Error changing participant status';
        this.snackbarColor = 'error';
        this.snackbar = true;
      } finally {
        this.processingAction = false;
      }
    },
    onSeeJournal(participant) {
      let sharingDetails = {
        id: participant.userId,
        username: participant.username,
        imageUrl: participant.imageUrl,
        firstName: participant.firstName,
        lastName: participant.lastName,
        country: participant.country,
        allowComments: false,
      };
      this.setShare(sharingDetails);
      this.$router.push('/book/shared');
    },
    exportCSV() {
      let rows = [['Trader First Name', 'Trader Last Name', 'Trader Email', 'Account ID', 'Score', 'Status']];
      this.$store.state.tournament.participants.forEach((participant) => {
        rows.push([
          `${participant.firstName}`,
          `${participant.lastName}`,
          `${participant.email}`,
          `${participant.brokerAccountIdentifier || ''}`,
          `${(participant.score === null) ? '0' : participant.score}`,
          `${participant.status}`,
        ]);
      });
      let csvContent = "data:text/csv;charset=utf-8," + rows.map(e => e.join(",")).join("\n");
      let encodedUri = encodeURI(csvContent);
      let link = document.createElement("a");
      link.setAttribute("href", encodedUri);
      link.setAttribute("download", "Tournament Participants.csv");
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      this.snackbarText = this.$t('downloadSuccess') || `Downloaded ${rows.length - 1} participants`;
      this.snackbarColor = 'mainGreen';
      this.snackbar = true;
    },
  },
  render(h) {
    return h('div', { key: this.refreshKey }, [
      h('keep-alive', this.$slots.default)
    ]);
  }
}
</script>

<style lang="scss" scoped>
.tournament-rank-view {
  font-family: "Avenir", sans-serif;

  .tournament-timer {
    @media screen and (max-width: 600px) {
      justify-content: center;
    }

    justify-content: flex-end;
  }

  // Keep existing styles for tournament-rank-embed
  &.tournament-rank-embed {
    padding: 0;
    margin: 0;

    // Hide unnecessary elements in embed view
    .admin-controls-bar,
    .admin-table-toolbar,
    .additional-desktop {
      display: none !important;
    }
  }

  /* New improved styles similar to tournament-embed-view */
  .tournament-embed-header {
    position: relative;

    .tournament-title {
      font-size: 24px;
      margin: 0;
    }

    .tournament-organizer {
      font-size: 16px;
      margin-top: 10px;
      color: #666;
    }

    /* Enhanced Tournament Timer Container */
    .tournament-timer-container {
      margin-top: -96px;
      margin-bottom: 100px;

      // Small tablet breakpoint (sm: 600px - 768px)
      @media (min-width: 600px) and (max-width: 768px) {
        margin-top: 10px;
        margin-bottom: 40px;
      }

      // Large tablet breakpoint (md: 768px - 960px)
      @media (min-width: 768px) and (max-width: 960px) {
        margin-top: -20px;
        margin-bottom: 60px;
      }

      // Mobile breakpoint
      @media (max-width: 600px) {
        margin-top: 20px;
        margin-bottom: 20px;
      }
    }

    /* Admin controls - redesigned bar matching Figma */
    .admin-controls-bar {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 8px 0;
      margin-bottom: 8px;

      .admin-controls-left {
        display: flex;
        align-items: center;
      }

      .admin-controls-right {
        display: flex;
        align-items: center;
        gap: 8px;
      }

      .admin-breadcrumb {
        background: none;
        border: none;
        cursor: pointer;
        font-family: 'Avenir', sans-serif;
        font-size: 14px;
        color: #888;
        display: flex;
        align-items: center;
        padding: 4px 0;
        transition: color 0.2s ease;

        &:hover {
          color: #333;
        }

        i {
          font-size: 13px;
          color: #888;
        }
      }

      .admin-icon-btn {
        background: none;
        border: none;
        width: 32px;
        height: 32px;
        display: flex;
        align-items: center;
        justify-content: center;
        cursor: pointer;
        color: #666;
        transition: color 0.2s ease;

        &:hover:not(:disabled) {
          color: #333;
        }

        &:disabled {
          opacity: 0.4;
          cursor: not-allowed;
        }

        i {
          font-size: 16px;
        }
      }

      .admin-divider {
        display: inline-block;
        width: 1px;
        height: 20px;
        background-color: #ddd;
        margin: 0 4px;
      }

      /* "View as User" / "View as Admin" — dark pill button */
      .admin-view-toggle {
        background-color: #ebebeb;
        color: #14161B;
        border: none;
        border-radius: 8px;
        padding: 8px 18px;
        font-family: 'Aeonik', sans-serif;
        font-size: 13px;
        font-weight: 500;
        cursor: pointer;
        display: flex;
        align-items: center;
        transition: all 0.2s ease;
        white-space: nowrap;

        &:hover {
          background-color: #ddd;
        }

        i {
          font-size: 14px;
        }
      }
    }
  }

  /* Disqualify confirm dialog */
  .disqualify-confirm-dialog {
    font-family: 'Avenir', sans-serif;
  }

  /* Status Chip Styling */
  .status-chip {
    padding: 6px 14px;
    border-radius: 25px;
    font-size: 13px;
    text-transform: capitalize;
    white-space: nowrap;

    &.still-open {
      background-color: #FFF75F;
    }

    &.in-progress {
      background-color: var(--v-mainGreen-base);
    }

    &.reviewing {
      background-color: #FF9800;
    }

    &.closed {
      background-color: #111111;
    }

    &.archived {
      background-color: #6c757d;
    }
  }

  /* Organizer Logo Styling */
  .organizer-logo-container {
    margin-bottom: 20px;

    .organizer-logo {
      max-height: 80px;
      max-width: 250px;
      object-fit: contain;
    }
  }

  /* Mobile Tournament Header Styling */
  .mobile-tournament-header {
    padding: 0 15px;
    position: relative;
    margin-bottom: 20px;

    .mobile-tournament-title-container {
      margin: 20px 0 15px;
      position: relative;
    }

    .mobile-tournament-title {
      font-size: 24px;
      font-weight: bold;
      text-align: center;
      margin: 0;
      padding: 0 10px 10px;
      color: var(--v-mainBlack-base);
      line-height: 1.3;
      word-wrap: break-word;
    }

    .mobile-title-underline {
      height: 3px;
      width: 60px;
      background: var(--v-mainGreen-base);
      margin: 5px auto 0;
      border-radius: 3px;
    }

    .mobile-tournament-organizer {
      font-size: 15px;
      text-align: center;
      color: #666;
      margin-bottom: 5px;

      .organizer-name {
        color: var(--v-mainBlack-base);
        font-weight: 500;
      }
    }

    .mobile-admin-controls {
      display: flex;
      justify-content: center;
      margin: 12px 0 8px;
    }

    .organizer-logo-container {
      margin-top: 10px;

      .organizer-logo {
        max-height: 60px;
        max-width: 200px;
        object-fit: contain;
      }
    }
  }

  /* Mobile Leaderboard Header */
  .mobile-leaderboard-header {
    width: 100%;
    text-align: center;

    .leaderboard-title-container {
      display: flex;
      align-items: center;
      justify-content: center;
      margin-bottom: 5px;

      i {
        font-size: 18px;
        color: var(--v-mainBlack-base);
        margin-right: 8px;
      }

      h2 {
        font-size: 20px !important;
        font-weight: bold;
        margin: 0;
      }
    }
  }

  /* Mobile Footer */
  .mobile-embed-footer {
    margin-top: 20px;
    width: 100%;
    background-color: transparent;

    .footer-actions {
      display: flex;
      justify-content: space-around;

      .footer-action-btn {
        background-color: transparent !important;
        border: 1px solid #e0e0e0;
        padding: 12px 20px;
        border-radius: 8px;
        font-size: 14px;
        font-weight: 500;
        display: flex;
        align-items: center;
        justify-content: center;
        cursor: pointer;
        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
        transition: all 0.2s ease;
        min-width: 120px;

        &:active {
          transform: translateY(1px);
          box-shadow: 0 0 2px rgba(0, 0, 0, 0.1);
        }

        i {
          font-size: 16px;
        }
      }
    }
  }

  /* Desktop Footer - new styles */
  .desktop-embed-footer {
    margin-top: 20px;

    .desktop-action-btn {
      background-color: var(--v-fullWhite-base);
      border: 1px solid #e0e0e0;
      padding: 10px 16px;
      border-radius: 8px;
      font-size: 14px;
      font-weight: 500;
      display: flex;
      text-align: center;
      align-items: center;
      justify-content: center;
      flex-grow: 1;
      transition: all 0.2s ease;
      cursor: pointer;

      &:hover {
        opacity: 0.9;
      }

      i {
        font-size: 16px;
      }
    }
  }

  /* No participant message */
  .no-participant-message {
    text-align: center;
    background-color: var(--v-lightGray-base);
    border-radius: 8px;
    padding: 16px;
    margin: 0 0 16px;

    p {
      margin: 0;
      font-size: 16px;
    }
  }

  /* Responsive utilities */
  .hide-on-mobile {
    @media (max-width: 600px) {
      display: none !important;
    }
  }

  .show-on-mobile {
    @media (min-width: 601px) {
      display: none !important;
    }
  }

  /* Rules drawer styles */
  .rules-drawer {
    z-index: 99999 !important;

    @media (max-width: 600px) {
      width: 100% !important;
    }
  }

  /* Share drawer styles */
  .share-drawer {
    z-index: 99999 !important;

    @media (max-width: 600px) {
      width: 100% !important;
    }
  }

  /* Manual Closure Dialog Styles */
  ::v-deep(.manual-closure-dialog) {
    .v-card__title.manual-closure-title {
      font-family: "Aeonik", "Avenir", sans-serif !important;
      font-weight: 500 !important;
      font-size: 20px !important;
      line-height: 1.4 !important;

      .v-icon {
        font-size: 20px;
      }
    }

    .v-card__text.manual-closure-body {
      font-family: "Avenir", sans-serif !important;

      p {
        font-family: "Avenir", sans-serif !important;
        font-size: 16px !important;
        line-height: 1.5 !important;
        color: var(--v-mainBlack-base) !important;
      }

      .v-alert {
        font-family: "Avenir", sans-serif !important;

        .v-alert__content {
          font-family: "Avenir", sans-serif !important;
          font-size: 14px !important;
          line-height: 1.5 !important;
        }

        .v-alert__wrapper {
          font-family: "Avenir", sans-serif !important;
        }
      }
    }

    .v-card__actions {
      padding: 16px 24px;

      .cancel-btn {
        font-family: "Avenir", sans-serif !important;
        font-weight: 500;
        text-transform: none;
        letter-spacing: 0;

        ::v-deep(.v-btn__content) {
          font-family: "Avenir", sans-serif !important;
        }
      }

      .confirm-btn {
        font-family: "Avenir", sans-serif !important;
        font-weight: 500;
        text-transform: none;
        letter-spacing: 0;

        ::v-deep(.v-btn__content) {
          font-family: "Avenir", sans-serif !important;
        }
      }
    }
  }
}

</style>

<style lang="scss">
/* Dark mode overrides (unscoped so .theme--dark ancestor selector works) */
.theme--dark {
  .admin-view-toggle {
    background-color: #303740 !important;
    color: #fff !important;

    &:hover {
      background-color: #3a424d !important;
    }
  }
}

</style>